import { useState, useEffect, useCallback } from "react";
import { FileText, Download, Trash2, Plus, Filter, CreditCard, Shield, Car, Loader2 } from "lucide-react";
import api from "../../../services/api";
import UploadDocumentModal from "./UploadDocumentModal";

var DOC_TYPE_LABELS = { boleto: "Boleto", apolice: "Apólice", consorcio_doc: "Consórcio" };
var DOC_TYPE_COLORS = { boleto: "bg-amber-100 text-amber-700", apolice: "bg-[#3B82F6]/10 text-[#3B82F6]", consorcio_doc: "bg-[#F26A4B]/10 text-[#F26A4B]" };
var DOC_TYPE_ICONS = { boleto: CreditCard, apolice: Shield, consorcio_doc: Car };

function formatCurrency(v) {
  if (!v || v === 0) return "-";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return "-"; }
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function DocumentsSection({ clientId, consortiums, policies }) {
  var docsState = useState([]);
  var docs = docsState[0];
  var setDocs = docsState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var showUploadState = useState(false);
  var showUpload = showUploadState[0];
  var setShowUpload = showUploadState[1];
  var filterState = useState("");
  var filter = filterState[0];
  var setFilter = filterState[1];
  var deletingState = useState(null);
  var deleting = deletingState[0];
  var setDeleting = deletingState[1];
  var downloadingState = useState(null);
  var downloading = downloadingState[0];
  var setDownloading = downloadingState[1];

  var fetchDocs = useCallback(async function() {
    setLoading(true);
    try {
      var url = "/documents/client/" + clientId;
      if (filter) url += "?document_type=" + filter;
      var res = await api.get(url);
      setDocs(res.data.documents || []);
    } catch(err) {
      console.error("Erro ao carregar documentos:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId, filter]);

  useEffect(function() { fetchDocs(); }, [fetchDocs]);

  async function handleDownload(doc) {
    setDownloading(doc.id);
    try {
      var res = await api.get("/documents/" + doc.id + "/download", { responseType: "blob" });
      var url = window.URL.createObjectURL(new Blob([res.data]));
      var a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch(err) {
      console.error("Erro ao baixar:", err);
    } finally {
      setDownloading(null);
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm("Excluir este documento?")) return;
    setDeleting(docId);
    try {
      await api.delete("/documents/" + docId);
      setDocs(function(prev) { return prev.filter(function(d) { return d.id !== docId; }); });
    } catch(err) {
      console.error("Erro ao excluir:", err);
    } finally {
      setDeleting(null);
    }
  }

  function handleUploadSuccess() {
    setShowUpload(false);
    fetchDocs();
  }

  var filters = [
    { value: "", label: "Todos" },
    { value: "boleto", label: "Boletos" },
    { value: "apolice", label: "Apolices" },
    { value: "consorcio_doc", label: "Consorcio" },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="documents-section">
      {showUpload && (
        <UploadDocumentModal
          clientId={clientId}
          consortiums={consortiums}
          policies={policies}
          onClose={function() { setShowUpload(false); }}
          onSuccess={handleUploadSuccess}
        />
      )}

      <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#3B82F6]/10 rounded-lg">
              <FileText className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#0A2F3D]">Documentos</h3>
              <p className="text-xs text-gray-400">{docs.length} documento{docs.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <button
            onClick={function() { setShowUpload(true); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg transition-colors"
            data-testid="upload-doc-btn"
          >
            <Plus className="w-3.5 h-3.5" />Enviar Documento
          </button>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          {filters.map(function(f) {
            var active = filter === f.value;
            return (
              <button
                key={f.value}
                onClick={function() { setFilter(f.value); }}
                className={"px-3 py-1.5 text-xs font-medium rounded-lg transition-colors " + (active ? "bg-[#3B82F6] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}
                data-testid={"filter-" + (f.value || "all")}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Nenhum documento encontrado</p>
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map(function(doc) {
              var Icon = DOC_TYPE_ICONS[doc.document_type] || FileText;
              var typeCls = DOC_TYPE_COLORS[doc.document_type] || "bg-gray-100 text-gray-600";
              var typeLabel = DOC_TYPE_LABELS[doc.document_type] || doc.document_type;

              return (
                <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100" data-testid={"doc-row-" + doc.id}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={"p-2 rounded-lg flex-shrink-0 " + (DOC_TYPE_COLORS[doc.document_type] ? DOC_TYPE_COLORS[doc.document_type].split(" ")[0] : "bg-gray-100")}>
                      <Icon className={"w-4 h-4 " + (DOC_TYPE_COLORS[doc.document_type] ? DOC_TYPE_COLORS[doc.document_type].split(" ")[1] : "text-gray-600")} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0A2F3D] truncate">{doc.file_name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className={"px-2 py-0.5 text-xs font-medium rounded-full " + typeCls}>{typeLabel}</span>
                        {doc.product_description && <span className="text-xs text-gray-400 truncate max-w-[200px]">{doc.product_description}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                    {doc.value > 0 && (
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Valor</p>
                        <p className="text-sm font-semibold text-[#0A2F3D]">{formatCurrency(doc.value)}</p>
                      </div>
                    )}
                    {doc.due_date && (
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Vencimento</p>
                        <p className="text-sm font-medium text-[#0A2F3D]">{formatDate(doc.due_date)}</p>
                      </div>
                    )}
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-400">Tamanho</p>
                      <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={function() { handleDownload(doc); }}
                        className="p-2 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-lg transition-colors"
                        title="Baixar"
                        data-testid={"download-doc-" + doc.id}
                      >
                        {downloading === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={function() { handleDelete(doc.id); }}
                        className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                        data-testid={"delete-doc-" + doc.id}
                      >
                        {deleting === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
